import { zodResolver } from '@hookform/resolvers/zod';
import {
  AggregateUsageEvent,
  IUsageEvent,
  UsageAggregateEventListRequestSchema,
} from '@monetize/types/app';
import {
  DEFAULT_PAGER,
  FilterType,
  FilterTypeOperator,
  TDataTablePager,
} from '@monetize/types/common';
import {
  replaceUserTimezoneWithUtc,
  toDateTimeShort,
} from '@monetize/utils/core';
import { createColumnHelper } from '../../../components/Monetize/DataTableNew/appTable';
import { addDays } from 'date-fns/addDays';
import { endOfDay } from 'date-fns/endOfDay';
import { formatISO } from 'date-fns/formatISO';
import { fromUnixTime } from 'date-fns/fromUnixTime';
import { startOfDay } from 'date-fns/startOfDay';
import React, { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import {
  useGetUsageAggregateRecordsInfiniteLoad,
  useGetUsageRecordsInfiniteLoad,
} from '../../../api/usageService';
import { MFlex } from '../../../components/Monetize';
import DataTableActions from '../../../components/Monetize/DataTable/MDataTableActions';
import { SELECTION_COL } from '../../../components/Monetize/DataTableNew/dataTableNewRenderers';
import {
  AccountLookupPopover,
  SubscriptionLookupPopover,
  UsageTypeLookupPopover,
} from '../../../components/Monetize/LookupPopover';
import { useUsageEventCustomFields } from '../../../hooks/useUsageEventCustomFields';
import { useFlags } from '../../../services/launchDarkly';
import {
  collectPropertyKeys,
  mergePropertyColumnKeys,
} from '../../AccountDetails/Subscriptions/Usage/usageProperties.utils';
import { getUsagePropertyColumns } from '../../AccountDetails/Subscriptions/Usage/usagePropertyColumns';

const aggregateColumnHelper = createColumnHelper<AggregateUsageEvent>();
const individualColumnHelper = createColumnHelper<IUsageEvent>();

const aggregateEventColumns = [
  aggregateColumnHelper.accessor('accountId', {
    header: 'Account ID',
    cell: (info) => <AccountLookupPopover value={info.getValue()} />,
    enableSorting: false,
    size: 215,
    minSize: 215,
  }),
  aggregateColumnHelper.accessor('subscriptionId', {
    header: 'Subscription ID',
    cell: (info) => (
      <SubscriptionLookupPopover
        value={info.getValue()}
        record={info.row.original}
      />
    ),
    enableSorting: false,
    size: 180,
    minSize: 180,
  }),
  aggregateColumnHelper.accessor('usageTypeId', {
    header: 'Usage Type ID',
    cell: (info) => <UsageTypeLookupPopover value={info.getValue()} />,
    enableSorting: false,
    size: 180,
    minSize: 180,
  }),
  aggregateColumnHelper.accessor('unitsConsumed', {
    header: 'Units',
    cell: (info) => info.getValue(),
    // This allows the column to fill the remaining space
    enableResizing: false,
    minSize: 131,
  }),
];

const baseIndividualEventColumns = [
  individualColumnHelper.accessor('id', {
    header: 'ID',
    cell: (info) => info.getValue(),
    size: 220,
    minSize: 180,
  }),
  individualColumnHelper.accessor('timestamp', {
    header: 'Timestamp (UTC)',
    cell: (info) => toDateTimeShort(fromUnixTime(info.getValue()), 'UTC'),
    size: 220,
    minSize: 180,
  }),
  // Ignore last column since we need to adjust a property there
  ...aggregateEventColumns.slice(0, -1),
  aggregateColumnHelper.accessor('unitsConsumed', {
    header: 'Units',
    cell: (info) => info.getValue(),
    minSize: 131,
  }),
];

const ratedPriceColumn = individualColumnHelper.accessor('ratedPrice', {
  header: 'Rated Price',
  cell: (info) => info.getValue(),
  size: 150,
  minSize: 180,
  enableResizing: false,
});

export const useTenantUsage = ({
  type,
  onVoidRecord,
  onEditRecord,
}: {
  type: 'AGGREGATE' | 'INDIVIDUAL';
  onVoidRecord?: (record: IUsageEvent) => void;
  onEditRecord?: (record: IUsageEvent) => void;
}) => {
  const [pager] = useState<TDataTablePager>(() => ({
    ...DEFAULT_PAGER,
    rows: 100,
    sortField: 'timestamp',
    sortOrder: 1,
  }));

  const periodDefaultStartDate = replaceUserTimezoneWithUtc(
    formatISO(startOfDay(addDays(new Date(), -30))),
  ).toISOString();

  const periodDefaultEndDate = replaceUserTimezoneWithUtc(
    formatISO(endOfDay(new Date())),
  ).toISOString();

  const formMethods = useForm({
    resolver: zodResolver(UsageAggregateEventListRequestSchema),
    mode: 'onChange',
    defaultValues: {
      startTime: periodDefaultStartDate,
      endTime: periodDefaultEndDate,
    },
  });

  const { watch, formState } = formMethods;

  const startTime = watch('startTime');
  const endTime = watch('endTime');

  const formattedStartTime =
    replaceUserTimezoneWithUtc(startTime).toISOString();
  const formattedEndTime = replaceUserTimezoneWithUtc(endTime).toISOString();

  const getUsageRecordsQuery = useGetUsageRecordsInfiniteLoad(
    {
      startTime: formattedStartTime,
      endTime: formattedEndTime,
    },
    pager,
    {
      enabled: formState.isValid && type === 'INDIVIDUAL',
      meta: { showErrorToast: true },
    },
  );

  const getAggregateUsageRecordsQuery = useGetUsageAggregateRecordsInfiniteLoad(
    {
      startTime: formattedStartTime,
      endTime: formattedEndTime,
    },
    pager,
    {
      enabled: formState.isValid && type === 'AGGREGATE',
      meta: { showErrorToast: true },
    },
  );

  const activeQuery =
    type === 'INDIVIDUAL'
      ? getUsageRecordsQuery
      : getAggregateUsageRecordsQuery;

  const usageRecords = useMemo(
    () => activeQuery.data?.pages.flatMap((page) => page.content) || [],
    [activeQuery.data?.pages],
  );
  const totalElements = activeQuery.data?.pages?.[0].totalElements || 0;

  const { enableUsageCustomFields } = useFlags();
  const { configuredKeys, customFieldsByKey } = useUsageEventCustomFields();
  const propertyKeys = useMemo(() => {
    if (!enableUsageCustomFields || type !== 'INDIVIDUAL') {
      return [];
    }
    return mergePropertyColumnKeys(
      configuredKeys,
      collectPropertyKeys(usageRecords as IUsageEvent[]),
    );
  }, [enableUsageCustomFields, type, configuredKeys, usageRecords]);
  const propertyKeysSignature = propertyKeys.join('|');

  const individualEventColumns = useMemo(() => {
    return [
      ...baseIndividualEventColumns,
      ...getUsagePropertyColumns(
        individualColumnHelper,
        propertyKeys,
        customFieldsByKey,
      ),
      ratedPriceColumn,
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [propertyKeysSignature, customFieldsByKey]);

  const filters = useMemo((): FilterType[] => {
    return [
      {
        key: 'startTime',
        value: formattedStartTime,
        operator: FilterTypeOperator.EQUAL,
      },
      {
        key: 'endTime',
        value: formattedEndTime,
        operator: FilterTypeOperator.EQUAL,
      },
      // BE supports these for aggregate - we could add search capability later
      // {
      //   key: 'accountIds',
      //   value: payload.accountIds?.join(',') || '',
      //   operator: FilterTypeOperator.EQUAL,
      // },
      // {
      //   key: 'subscriptionIds',
      //   value: payload.subscriptionIds?.join(',') || '',
      //   operator: FilterTypeOperator.EQUAL,
      // },
      // {
      //   key: 'usageTypeIds',
      //   value: payload.usageTypeIds.join(','),
      //   operator: FilterTypeOperator.EQUAL,
      // },
    ];
  }, [formattedEndTime, formattedStartTime]);

  const columns = useMemo(() => {
    if (type === 'AGGREGATE') {
      return aggregateEventColumns;
    }

    const hasActions = onVoidRecord || onEditRecord;
    if (!hasActions) {
      return individualEventColumns;
    }

    return [
      { ...SELECTION_COL, enableColumnFilter: false },
      ...individualEventColumns,
      individualColumnHelper.display({
        id: 'actions',
        size: 32,
        enableSorting: false,
        cell: ({ row }) => {
          const actions: React.ComponentProps<
            typeof DataTableActions
          >['actions'] = [];
          if (onEditRecord) {
            actions.push({
              title: 'Edit',
              enabled: true,
              action: () => onEditRecord(row.original),
            });
          }
          if (onVoidRecord) {
            actions.push({
              title: 'Void',
              enabled: true,
              color: 'danger' as const,
              action: () => onVoidRecord(row.original),
            });
          }
          return (
            <MFlex align="center" justify="flex-end">
              <DataTableActions actions={actions} />
            </MFlex>
          );
        },
      }),
    ];
  }, [type, onVoidRecord, onEditRecord, individualEventColumns]);

  return {
    formMethods,
    queryResults: activeQuery,
    usageRecords,
    totalElements,
    columns,
    filters,
    pager,
  };
};
